import { useEffect, useRef, useState } from 'react';
import { AutoSizer, Collection, WindowScroller } from 'react-virtualized';

import useScreenSize from '@/hooks/useScreenSize';
import ArtworkCard from '@/shared-components/cards/components/ArtworkCard';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';

import "./styles/VirtualList.css";

export default function VirtualList({ items, onLoadMore, navigateToArtwork, fromSearch }) {
  const screenSize = useScreenSize();
  const [measuredCollectionWidth, setMeasuredCollectionWidth] = useState(0);
  const columnSizesCache = useRef(initColumnSizesCache());
  const sizeAndPositionCache = useRef([]);
  const collectionRef = useRef();

  function initColumnSizesCache() {
    const columnCount = fromSearch
      ? (screenSize.width <= 900
        ? 2
        : screenSize.width <= 1700
          ? 3
          : screenSize.width <= 2600
            ? 4
            : 5)
      : (screenSize.width <= 900
        ? 2
        : screenSize.width <= 1700
          ? 3
          : screenSize.width <= 2600
            ? 4
            : 5);
    const spacer = fromSearch
      ? (screenSize.width <= 600 ? 10 : 15)
      : 15;
    const fallbackListWidth = screenSize.width <= 650 ? screenSize.width-22 : screenSize.width <= 1004 ? screenSize.width-40 : screenSize.width <= 1400 ? screenSize.width-60 : Math.min(1720, screenSize.width - 80);
    const widthOfList = fromSearch && measuredCollectionWidth > 0
      ? measuredCollectionWidth
      : fallbackListWidth;
    const columnWidth = (widthOfList - spacer*(columnCount-1)) / columnCount;

    let columnStarts = [];
    let columnHeights = [];

    for(let i=0; i<columnCount; i++) {
      if(i===0) columnStarts[i] = 0;
      else columnStarts[i] = columnStarts[i-1] + columnWidth + spacer;
      columnHeights[i] = 0;
    }

    return { columnCount, columnWidth, spacer, columnStarts, columnHeights };
  }

  function cellSizeAndPositionGetter({ index }) {
    if(!sizeAndPositionCache.current[index]) {
      const item = items?.data?.[index];
      const aspectRatio = item?.artwork_media?.artwork_main_picture?.image_original_data?.aspect_ratio;
      const safeAspectRatio = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1;
      const heightOfImage = columnSizesCache.current.columnWidth / safeAspectRatio;
      let heightOfItem = screenSize.width <= 550 ? heightOfImage + 48 : screenSize.width <= 1004 ? heightOfImage + 45.5 : heightOfImage + 71;
      heightOfItem += 15;
      const shortestColumn = columnSizesCache.current.columnHeights.indexOf(Math.min(...columnSizesCache.current.columnHeights));
      const item_x = columnSizesCache.current.columnStarts[shortestColumn];
      const item_y = columnSizesCache.current.columnHeights[shortestColumn];
    
      columnSizesCache.current.columnHeights[shortestColumn] += heightOfItem + columnSizesCache.current.spacer;

      const calculatedSizeAndPosition = {
        width: columnSizesCache.current.columnWidth,
        height: heightOfItem,
        x: item_x,
        y: item_y
      };

      sizeAndPositionCache.current[index] = calculatedSizeAndPosition;

      return calculatedSizeAndPosition;
    } else {
      return sizeAndPositionCache.current[index];
    }
  }
    
  useEffect(() => {
    columnSizesCache.current = initColumnSizesCache();
    sizeAndPositionCache.current = [];
    collectionRef.current?.recomputeCellSizesAndPositions();
  }, [fromSearch, measuredCollectionWidth, screenSize.width]);

  return (
    <>
      <InfiniteScroll 
        onLoadMore={onLoadMore} 
        shouldLoadMore={items.data.length < items.totalDocs} 
        listLength={items.data.length} 
        className={`home-artworks__infinite-container${fromSearch ? " home-artworks__infinite-container--from-search" : ""}`}
      >
        {({ infiniteScrollAssigner }) => (
          <WindowScroller>
            {({ height, scrollTop }) => (
              <AutoSizer
                disableHeight
                onResize={({ width }) => {
                  if (!fromSearch) {
                    return;
                  }

                  setMeasuredCollectionWidth((currentWidth) => (
                    currentWidth === width ? currentWidth : width
                  ));
                }}
              >
                {({ width }) => (
                  <Collection
                    autoHeight
                    width={width === 0 ? 0.1 : width}
                    height={height}
                    scrollTop={scrollTop}
                    ref={collectionRef}
                    cellCount={items.data.length}
                    cellSizeAndPositionGetter={cellSizeAndPositionGetter}
                    className="remove-scrollbar"
                    cellRenderer={({ index, key, style }) => (
                      <div key={key} style={style} ref={infiniteScrollAssigner(index)}>
                        <ArtworkCard 
                          artworkData={items.data[index]}
                          onClick={() => {
                            navigateToArtwork(items.data[index].slug);
                          }}
                        />
                      </div>
                    )}
                  />
                )}
              </AutoSizer>
            )}
          </WindowScroller>
        )}
      </InfiniteScroll>
    </>
  );
}
